import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert } from 'react-bootstrap';
import { useSearchParams, Link } from 'react-router-dom';
import { authApiService } from '../services/apiServices';
import { authService } from '../services/authService';

interface LoginFormProps {
  onSuccess: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [searchParams] = useSearchParams();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Получаем returnUrl из query-параметров
  const redirectParam = searchParams.get('redirect');

  useEffect(() => {
    // Если уже авторизованы - редиректим на целевую страницу
    if (authService.isAuthenticated()) {
      // Приоритет: query-параметр > localStorage > /profile
      const targetUrl = redirectParam || authService.getReturnUrl() || '/profile';
      authService.clearReturnUrl();

      // Используем window.location.href для полной перезагрузки
      if (targetUrl !== '/login') {
        window.location.href = targetUrl;
      } else {
        window.location.href = '/profile';
      }
    } else if (redirectParam && redirectParam !== '/login') {
      // Если не авторизованы, но есть query-параметр - сохраняем его в localStorage
      authService.setReturnUrl(redirectParam);
    }
  }, [redirectParam]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authApiService.signIn(login, password);

      // Проверяем, что токен действительно установлен
      if (authService.isAuthenticated()) {
        // Получаем returnUrl до очистки localStorage
        const savedReturnUrl = authService.getReturnUrl();

        // Приоритет: query-параметр > localStorage > /profile
        const returnUrl = redirectParam || savedReturnUrl || '/profile';

        // Очищаем returnUrl из localStorage
        authService.clearReturnUrl();

        // Редиректим на целевую страницу (избегаем редиректа на /login)
        // Используем replace для немедленного редиректа без записи в историю
        if (returnUrl !== '/login') {
          window.location.replace(returnUrl);
        } else {
          window.location.replace('/profile');
        }

        // Останавливаем выполнение кода после редиректа
        return;
      } else {
        setError('Ошибка аутентификации: токен не был установлен');
      }
    } catch (err: any) {
      console.error('Ошибка входа:', err);
      let errorMessage = 'Ошибка входа. Пожалуйста, проверьте логин и пароль.';

      // Попробуем получить сообщение об ошибке из ответа сервера
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="shadow w-100" style={{ maxWidth: '400px', margin: '0 auto' }}>
      <Card.Header className="bg-primary text-white text-center py-3">
        <Card.Title className="mb-0">
          <h3 className="mb-0">Вход в админ панель</h3>
        </Card.Title>
      </Card.Header>
      <Card.Body className="p-4">
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="formLogin">
            <Form.Label className="fw-bold">Логин</Form.Label>
            <Form.Control
              type="text"
              placeholder="Введите логин"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              required
              disabled={loading}
              className="py-2"
            />
          </Form.Group>

          <Form.Group className="mb-4" controlId="formPassword">
            <Form.Label className="fw-bold">Пароль</Form.Label>
            <Form.Control
              type="password"
              placeholder="Введите пароль"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              className="py-2"
            />
            <div className="text-end mt-1">
              <Link to="/forgot-password" className="text-decoration-none small">
                Забыли пароль?
              </Link>
            </div>
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="w-100 py-2 fw-semibold"
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Вход...
              </>
            ) : (
              'Войти'
            )}
          </Button>
        </Form>

        <div className="text-center mt-3">
          <Link to="/register" className="text-decoration-none small">
            Зарегистрироваться
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
};

export default LoginForm;
